import type { Midi } from "@midi-fantom-utils/midi-core";
import { formatDurationWithMs } from "./format.js";
import { collectNotesForKeyDetection, detectKey } from "./key-detect.js";
import type { Report, ReportControlChange, TrackReport } from "./types.js";

export const buildReport = (midi: Midi, fileName = ""): Report => {
  const tracks: TrackReport[] = midi.tracks.map((track, index) => {
    const controlChanges: ReportControlChange[] = [...track.controlChanges.entries()]
      .filter(([number]) => Number.isFinite(number))
      .sort((a, b) => a[0] - b[0])
      .map(([number, list]) => ({
        number,
        count: list.length,
      }));

    return {
      index,
      channel: track.channel,
      instrument: {
        family: track.instrument.family,
        number: track.instrument.number,
        name: track.instrument.name,
        percussion: track.instrument.percussion,
      },
      hasNotes: track.notes.length > 0,
      noteCount: track.notes.length,
      programChangeCount: track.programChanges.length,
      controlChanges,
      pitchBendCount: track.pitchBends.length,
      metaCount: track.meta.length,
      unknownMetaCount: track.unknownMeta.length,
    };
  });

  return {
    fileName,
    duration: formatDurationWithMs(midi.header.durationSeconds),
    durationTicks: computeDurationTicks(midi),
    tempos: midi.header.tempos.map((t) => ({
      bpm: t.bpm,
      ticks: t.ticks,
    })),
    timeSignatures: midi.header.timeSignatures.map((ts) => ({
      value: ts.timeSignature.join("/"),
      ticks: ts.ticks,
    })),
    keySignatures: midi.header.keySignatures.map((ks) => ({
      key: ks.key,
      scale: ks.scale,
      ticks: ks.ticks,
    })),
    detectedKey: detectKey(collectNotesForKeyDetection(midi)),
    tracks,
  };
};

const computeDurationTicks = (midi: Midi): number => {
  let last = 0;
  for (const track of midi.tracks) {
    for (const note of track.notes) {
      const end = note.ticks + note.durationTicks;
      if (end > last) last = end;
    }
  }
  return last;
};